import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { calculateContainerHeight, calculateFontSize, formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import Legend from 'components/molecules/Legend';
import { graphItemContainerClass, subHeaderClass } from 'components/pages/PVMAnalysis';
import InfiniteChartComponent from 'components/molecules/InfiniteChartComponent/InfiniteChartComponent';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { UserContext } from 'contexts/ConfigContext';
import { useFetchAttributeLevelElasticity } from 'hooks/opportunityIdentification/useFetchAttributeLvlElasticity';
import { FilterContextData } from 'contexts/FilterContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { OPPORTUNITY_IDENTIFICATION_CHART2_VALUE_SELECTED } from 'utils/mixpanelEventNames';

type ElasticityGroupBarChart1Props = {
    selectedFirstChartItem: any;
    selectedChartItem: any;
    setSelectedChartItem: Function;
    selectedLevel: any;
    intersectionFilter: any;
    loading: boolean;
    showError: boolean;
};

const ElasticityGroupBarChart2 = (props: ElasticityGroupBarChart1Props) => {
    const {
        selectedFirstChartItem,
        selectedChartItem,
        setSelectedChartItem,
        selectedLevel,
        intersectionFilter,
        loading,
        showError,
    } = props;
    const { appConfig } = useContext(UserContext);
    const elasticityConfig = appConfig.opportunityIdentification.elasticityDriver;
    const commonConfig = getConfig('commonConfig', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const showFullLabel = commonConfig?.showFullLabel;

    const chart1OffsetRef = useRef(elasticityConfig.offset || 0);
    const chart1Ref = useRef<any>();

    const { allDimAndAttributeTypeList, idForRestOfComponent, timePeriodSelection, idForRestOfComponentLoading } =
        useContext(FilterContextData);

    const {
        data: chart2Data,
        loading: chart2Loading,
        error: chart2Error,
        count: chart2Count,
        meta,
        fetchAttributeLevelElasticity,
    } = useFetchAttributeLevelElasticity();

    const variables = useMemo(() => {
        chart1OffsetRef.current = 0;
        if (!idForRestOfComponentLoading && intersectionFilter) {
            const filters = JSON.stringify(intersectionFilter);
            return {
                filters: filters,
                timePeriod: String(timePeriodSelection).toLowerCase(),
                dimension: selectedLevel.dimension,
                attributeType: selectedLevel.value,
                limit: elasticityConfig.limit,
                offset: chart1OffsetRef.current,
            };
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
        intersectionFilter,
        selectedLevel.value,
        selectedFirstChartItem,
    ]);

    useEffect(() => {
        fetchAttributeLevelElasticity({
            variables,
        });
        chart1OffsetRef.current = 0;
    }, [variables]);

    const combinedLoading = chart2Loading || loading || _.isEmpty(selectedFirstChartItem); // if first chart item is null, show loading
    const height = Math.max(450, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200

    const formattedChartLabel = `Base Price Elasticities by ${selectedLevel.label}`;

    //scale y axis w.r.t the max characters to avoid collision of names  (only for chart 2)
    const barHeight2WRTCharacters = Math.max(meta?.maxCharacters * (77 / 39) || 0, 77); // 39 characters can be accommodated in 77px height
    const barHeight2 = Math.max(
        barHeight2WRTCharacters,
        Array.isArray(chart2Data) && chart2Data?.length > 0 ? height / chart2Data?.length : 0,
    );
    const yAxisGraphWidth = calculateFontSize(window.innerWidth, 120);
    const containerWidth = useContainerWidth({
        containerId: 'random id2' + 'q', //giving an incorrect id so that it takes from main container by default
        buffer: 16 * 3 + 2, //padding 16 * 3 + border 2
    });

    //infinite scroll function
    function infiniteScrollOnNext(e: any) {
        if (chart2Loading) return;
        //finding the scrollbar position
        const scrollHeight = e?.scrollHeight; //total height
        const clientHeight = e?.clientHeight; //viewport height
        const scrollBarBottomPosition = clientHeight + e?.scrollTop;
        const threshold = 0.95;

        if (scrollBarBottomPosition >= scrollHeight * threshold && chart2Data?.length < meta.total) {
            const newOffset = elasticityConfig.limit + chart1OffsetRef.current;
            fetchAttributeLevelElasticity({
                variables: {
                    ...variables,
                    offset: newOffset,
                },
            });
            chart1OffsetRef.current = newOffset;
        }
    }

    //debounced infinite scroller function
    const debouncedInfiniteScroller = useCallback(_.debounce(infiniteScrollOnNext, 150), [
        chart2Count,
        chart2Data,
        combinedLoading,
    ]);

    const chartWidth = containerWidth / 2 - 16 * 3; //subtract inner padding for 2 charts

    return (
        <div className={`${graphItemContainerClass} mb-0 flex-1`}>
            {(chart2Error && !combinedLoading && !chart2Data) || showError ? (
                <div style={{ height: height + 178, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ErrorBox msg={'Insufficient data'} />
                </div>
            ) : // LOADING
            combinedLoading ? (
                <div style={{ height: height + 178 }}>
                    <Loader />
                </div>
            ) : (
                <div ref={chart1Ref}>
                    <div className="flex justify-between">
                        <p className={subHeaderClass}> {formattedChartLabel} </p>
                    </div>
                    <div className="flex items-center justify-between" style={{ marginTop: 14, marginBottom: 24 }}>
                        <div className="w-[35px]"></div>
                        <Legend data={elasticityConfig.legendMapping} />
                    </div>
                    <InfiniteChartComponent
                        isGroupedChart
                        rowCount={chart2Data?.length || 0}
                        minBound={meta?.minBound - 0.5}
                        maxBound={meta?.maxBound}
                        intervalFormatter={(val: any) => {
                            formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                        tooltipFormatter={(xAxis: any, val: any) => {
                            return {
                                name: xAxis,
                                value: formatNumWithKMBAlt(val, decimalForPercentage) + '%',
                            };
                        }}
                        intervalStyle={() => {
                            return {
                                cursor: 'pointer',
                            };
                        }}
                        height={height}
                        rowRenderer={(key, index, style) => {
                            return chart2Data && chart2Data[index];
                        }}
                        chartItemOnClick={(item: any) => {
                            setSelectedChartItem(item);
                            trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_CHART2_VALUE_SELECTED, {
                                attributeTypeName: item?.label,
                            });
                        }}
                        chartContainer={'drilldown-chart-virtualized2'}
                        barHeight={barHeight2}
                        chartWidth={chartWidth}
                        yAxisGraphWidth={yAxisGraphWidth}
                        selectedChartItem={selectedChartItem}
                        showFullLabel={showFullLabel}
                        debouncedInfiniteScroller={debouncedInfiniteScroller}
                        chartOffsetRef={chart1OffsetRef}
                        appConfig={appConfig}
                        fields={['category', 'sales']}
                        xAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default ElasticityGroupBarChart2;
